import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AiCostsCard from "@/components/AiCostsCard";
import SocialStatsCard from "@/components/SocialStatsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, TrendingUp, Heart, MessageSquare, Share, Eye, Users, RefreshCw, Loader2, Clock, FileDown, Trophy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ["#2563EB", "#2563EB", "#16A34A", "#D97706", "#7C3AED"];

export default function PerformancePage() {
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState("all");
  const [summary, setSummary] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [editingMetric, setEditingMetric] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [abStats, setAbStats] = useState(null);
  const { t } = useLanguage();

  const downloadPdfReport = async () => {
    if (filterCompany === "all") {
      toast.info("Bitte zuerst eine Firma auswaehlen fuer den PDF-Report");
      return;
    }
    setDownloadingPdf(true);
    try {
      const res = await axios.post(
        `${API}/reports/pdf`,
        { company_id: filterCompany, days: 30, include_top_posts: 10 },
        { withCredentials: true, responseType: "blob" }
      );
      const company = companies.find((c) => c.id === filterCompany);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `InvictaFlow_${(company?.name || "report").replace(/\s+/g, "_")}_${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF-Report heruntergeladen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-Erstellung fehlgeschlagen");
    }
    setDownloadingPdf(false);
  };


  const load = async () => {
    try {
      const params = filterCompany !== "all" ? { company_id: filterCompany } : {};
      const [s, m, c] = await Promise.all([
        axios.get(`${API}/performance/summary`, { params, withCredentials: true }),
        axios.get(`${API}/performance`, { params, withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setMetrics(m.data);
      setCompanies(c.data);
      if (filterCompany !== "all") {
        try {
          const ab = await axios.get(`${API}/performance/ab-test`, { params: { company_id: filterCompany }, withCredentials: true });
          setAbStats(ab.data);
        } catch { setAbStats(null); }
      } else {
        setAbStats(null);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [filterCompany]);

  useEffect(() => {
    axios.get(`${API}/scheduler/status`, { withCredentials: true })
      .then((r) => setSchedulerStatus(r.data)).catch(() => {});
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/performance/refresh`, {}, { withCredentials: true });
      toast.success(`${res.data.refreshed} Metriken aktualisiert`);
      load();
    } catch { toast.error("Aktualisierung fehlgeschlagen - pruefe Social Media Tokens"); }
    setRefreshing(false);
  };

  const updateMetric = async (id) => {
    try {
      await axios.put(`${API}/performance/${id}`, { metrics: editForm }, { withCredentials: true });
      toast.success("Metriken aktualisiert");
      setEditingMetric(null);
      load();
    } catch { toast.error("Fehler"); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";

  const platformData = summary?.by_platform ? Object.entries(summary.by_platform).map(([name, data]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    posts: data.count, likes: data.likes, comments: data.comments, reach: data.reach,
  })) : [];

  const statCards = summary ? [
    { label: t("performance.posts"), value: summary.total_posts, icon: BarChart3, color: "text-[#2563EB]" },
    { label: t("performance.likes"), value: summary.likes, icon: Heart, color: "text-pink-600" },
    { label: t("performance.comments"), value: summary.comments, icon: MessageSquare, color: "text-blue-600" },
    { label: t("performance.shares"), value: summary.shares, icon: Share, color: "text-green-600" },
    { label: t("performance.reach"), value: summary.reach, icon: Eye, color: "text-indigo-600" },
    { label: t("performance.engagement"), value: `${summary.engagement_rate}%`, icon: TrendingUp, color: "text-amber-600" },
  ] : [];

  return (
    <div data-testid="performance-page" className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">{t("performance.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{t("performance.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}
            data-testid="refresh-metrics-btn" className="h-9 text-xs">
            {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            {t("performance.refresh_btn")}
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs" data-testid="export-csv-btn"
            onClick={() => window.open(`${API}/export/performance/csv${filterCompany !== "all" ? `?company_id=${filterCompany}` : ""}`, "_blank")}>
            CSV Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            onClick={downloadPdfReport}
            disabled={downloadingPdf || filterCompany === "all"}
            data-testid="export-pdf-btn"
            title={filterCompany === "all" ? "Firma waehlen" : "Client-Report als PDF"}
          >
            {downloadingPdf ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileDown className="w-3 h-3 mr-1" />}
            PDF Report
          </Button>
          <Link to="/leaderboard">
            <Button variant="outline" size="sm" className="h-9 text-xs" data-testid="open-leaderboard-btn">
              <Trophy className="w-3 h-3 mr-1 text-amber-500" /> Leaderboard
            </Button>
          </Link>
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-48" data-testid="perf-company-filter">
              <SelectValue placeholder="Alle Firmen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <SocialStatsCard companyId={filterCompany} />

      <AiCostsCard companyId={filterCompany} />

      {/* Scheduler Status Banner */}
      {schedulerStatus && (
        <div className="bg-[#0F0F0F] text-white rounded-xl p-4 flex items-center justify-between text-sm" data-testid="scheduler-banner">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-[#2563EB]" />
            <div>
              <p className="font-medium">Auto-Scheduler aktiv (alle {schedulerStatus.interval_minutes} Min.)</p>
              <p className="text-white/50 text-xs">
                {schedulerStatus.scheduled_pending} geplante Inhalte wartend
                {schedulerStatus.last_run && ` · Letzter Lauf: ${new Date(schedulerStatus.last_run.timestamp).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`}
              </p>
            </div>
          </div>
          <Badge className="bg-green-500/20 text-green-400 border-0">Aktiv</Badge>
        </div>
      )}

      {/* Stats Grid */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {statCards.map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="border-[var(--studio-border)] shadow-none">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--studio-text-muted)]">{label}</p>
                    <p className="text-3xl font-bold font-heading mt-1">{value}</p>
                  </div>
                  <Icon className={`w-8 h-8 ${color} opacity-80`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Platform Chart */}
      {platformData.length > 0 && (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardHeader><CardTitle className="font-heading text-lg">Engagement nach Plattform</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={platformData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--studio-border)" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid var(--studio-border)" }} />
                <Bar dataKey="likes" name="Likes" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="comments" name="Kommentare" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="reach" name="Reichweite" fill="#16A34A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* A/B Caption-Stil-Test */}
      {abStats && (
        <Card className="border-[var(--studio-border)] shadow-none" data-testid="ab-test-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">A/B Caption-Stil-Test</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["A", "B"].map((v) => {
                const d = abStats.variants?.[v] || {};
                const isWinner = abStats.winner === v;
                return (
                  <div
                    key={v}
                    className={`p-4 rounded-lg border ${isWinner ? "border-emerald-400 bg-emerald-50" : "border-[var(--studio-border)] bg-[var(--studio-bg)]"}`}
                    data-testid={`ab-variant-${v}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">Variante {v}: {d.name}</p>
                      {isWinner && <span className="text-xs font-semibold text-emerald-700">🏆 Gewinner</span>}
                    </div>
                    <div className="flex gap-6 mt-2 text-xs text-[var(--studio-text-muted)]">
                      <span>Posts: <b className="text-[var(--studio-text)]">{d.posts || 0}</b></span>
                      <span>Ø Engagement: <b className="text-[var(--studio-text)]">{d.avg_engagement || 0}</b></span>
                      <span>Ø Reichweite: <b className="text-[var(--studio-text)]">{d.avg_reach || 0}</b></span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[var(--studio-text-muted)]">
              {abStats.winner
                ? `Die KI bevorzugt ab jetzt automatisch die Stärken von Variante ${abStats.winner} bei neuen Captions.`
                : `Jede neue Caption wird abwechselnd in Stil A (emotional) oder Stil B (direkt) erstellt. Ab ${abStats.min_posts_needed} veröffentlichten Posts pro Variante wird automatisch der Gewinner ermittelt und die KI lernt daraus.`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Top Content */}
      {summary?.top_content?.length > 0 && (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardHeader><CardTitle className="font-heading text-lg">Top-Content</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {summary.top_content.map((m, i) => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--studio-bg)]">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-[var(--studio-text-muted)] w-6">#{i + 1}</span>
                  <div>
                    <p className="text-sm font-medium">{companyName(m.company_id)}</p>
                    <p className="text-xs text-[var(--studio-text-muted)]">{m.platform} &middot; {new Date(m.published_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })}</p>
                  </div>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-pink-500" /> {m.metrics?.likes || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3 text-blue-500" /> {m.metrics?.comments || 0}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-green-500" /> {m.metrics?.reach || 0}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* All Metrics with Edit */}
      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Alle Metriken</CardTitle></CardHeader>
        <CardContent>
          {metrics.length === 0 ? (
            <p className="text-sm text-[var(--studio-text-muted)] text-center py-8">
              Noch keine Performance-Daten. Veroeffentliche Content, um Metriken zu tracken.
            </p>
          ) : (
            <div className="space-y-2">
              {metrics.map((m) => (
                <div key={m.id} className="p-3 rounded-lg bg-[var(--studio-bg)]" data-testid={`metric-${m.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{companyName(m.company_id)} &middot; {m.platform}</p>
                      <p className="text-xs text-[var(--studio-text-muted)]">{new Date(m.published_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-3 text-xs">
                        <span><Heart className="w-3 h-3 inline text-pink-500" /> {m.metrics?.likes || 0}</span>
                        <span><MessageSquare className="w-3 h-3 inline text-blue-500" /> {m.metrics?.comments || 0}</span>
                        <span><Eye className="w-3 h-3 inline text-green-500" /> {m.metrics?.reach || 0}</span>
                      </div>
                      <Button size="sm" variant="outline" className="text-xs h-7"
                        data-testid={`edit-metric-${m.id}`}
                        onClick={() => { setEditingMetric(m.id); setEditForm(m.metrics || {}); }}>
                        Bearbeiten
                      </Button>
                    </div>
                  </div>
                  {editingMetric === m.id && (
                    <div className="mt-3 pt-3 border-t border-[var(--studio-border)] animate-fade-in">
                      <div className="grid grid-cols-5 gap-2">
                        {["likes", "comments", "shares", "reach", "impressions"].map((k) => (
                          <div key={k}>
                            <Label className="text-[10px] text-[var(--studio-text-muted)] capitalize">{k}</Label>
                            <Input type="number" value={editForm[k] || 0} className="mt-0.5 h-8 text-xs"
                              onChange={(e) => setEditForm((f) => ({ ...f, [k]: parseInt(e.target.value) || 0 }))} />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-2 justify-end">
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setEditingMetric(null)}>Abbrechen</Button>
                        <Button size="sm" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs h-7" onClick={() => updateMetric(m.id)}>Speichern</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
